import BaseNotificationHandler from './baseHandler.js';

/**
 * Visit Notification Handler
 * Handles outpatient and inpatient visit notifications
 */
class VisitHandler extends BaseNotificationHandler {
  getIcon(notification, parsedData) {
    const notificationType = notification.type;
    if (notificationType.includes('outpatient')) {
      return "🏥";
    } else if (notificationType.includes('inpatient')) {
      return "🏨";
    }
    return "🏥";
  }

  getMainText(notification, parsedData) {
    const senderName = this.getSenderInfo(notification, parsedData) || 'Someone';
    const petName = parsedData.pet_name;
    const notificationType = notification.type;

    if (notificationType.includes('outpatient')) {
      if (notificationType === "outpatient_visit_created") {
        return petName
          ? `${senderName} created an outpatient visit for ${petName}`
          : `${senderName} created an outpatient visit`;
      } else if (notificationType === "outpatient_visit_updated") {
        return petName
          ? `${senderName} updated ${petName}'s outpatient visit`
          : `${senderName} updated an outpatient visit`;
      } else if (notificationType === "outpatient_visit_status_changed") {
        return petName
          ? `${senderName} changed ${petName}'s outpatient visit status`
          : `${senderName} changed outpatient visit status`;
      }
    } else if (notificationType.includes('inpatient')) {
      if (notificationType === "inpatient_visit_created") {
        return petName
          ? `${senderName} created an inpatient visit for ${petName}`
          : `${senderName} created an inpatient visit`;
      } else if (notificationType === "inpatient_visit_updated") {
        return petName
          ? `${senderName} updated ${petName}'s inpatient visit`
          : `${senderName} updated an inpatient visit`;
      } else if (notificationType === "inpatient_visit_status_changed") {
        return petName
          ? `${senderName} changed ${petName}'s inpatient visit status`
          : `${senderName} changed inpatient visit status`;
      }
    }

    return `${senderName} updated a visit`;
  }

  getDetails(notification, parsedData) {
    const details = [];
    const notificationType = notification.type;

    if (notificationType.includes('outpatient')) {
      if (parsedData.out_serial_num) {
        details.push(`Serial: ${parsedData.out_serial_num}`);
      }
    } else if (notificationType.includes('inpatient')) {
      if (parsedData.in_serial_num) {
        details.push(`Serial: ${parsedData.in_serial_num}`);
      }
    }

    if (parsedData.pet_name) {
      details.push(`Pet: ${parsedData.pet_name}`);
    }
    if (parsedData.clinic_name) {
      details.push(`Clinic: ${parsedData.clinic_name}`);
    }
    if (parsedData.visit_date) {
      try {
        const date = new Date(parsedData.visit_date);
        if (!isNaN(date.getTime())) {
          const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          details.push(formattedDate);
        }
      } catch (e) {
        // Ignore date parsing errors
      }
    }

    return details;
  }

  getThumbnail(notification, parsedData) {
    // For visits, try to get pet image
    if (parsedData.pet_image) return parsedData.pet_image;
    if (parsedData.image) return parsedData.image;
    if (parsedData.data?.pet_image) return parsedData.data.pet_image;
    return super.getThumbnail(notification, parsedData);
  }

  handleClick(notification, parsedData, navigate, onClose) {
    const notificationType = notification.type;
    const actionUrl = this.getActionUrl(notification, parsedData);
    
    // Get clinicId from parsedData, URL, or localStorage
    let clinicId = parsedData.clinic_id || parsedData.clinicId;
    if (!clinicId) {
      // Try to get from current URL
      const pathMatch = window.location.pathname.match(/\/medical\/clinic\/(\d+)/);
      if (pathMatch) {
        clinicId = pathMatch[1];
      } else {
        // Fallback to localStorage
        try {
          const userData = localStorage.getItem('user_data');
          if (userData && userData !== 'undefined' && userData !== 'null') {
            const parsed = JSON.parse(userData);
            clinicId = parsed.clinic_id;
          }
        } catch (e) {
          // Ignore errors
        }
      }
    }

    if (notificationType.includes('outpatient')) {
      const outpatientVisitId = parsedData.outpatient_visit_id || parsedData.visit_id || notification.target_id;
      if (outpatientVisitId) {
        sessionStorage.setItem('highlightOutpatientVisitId', outpatientVisitId.toString());
        const path = clinicId 
          ? `/medical/clinic/${clinicId}/appointment-management/outpatient`
          : '/medical/clinic/appointment-management/outpatient';
        navigate(path);
        if (onClose) onClose();
      } else if (actionUrl) {
        if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
          window.location.href = actionUrl;
        } else {
          navigate(actionUrl);
        }
        if (onClose) onClose();
      }
    } else if (notificationType.includes('inpatient')) {
      const inpatientVisitId = parsedData.inpatient_visit_id || notification.target_id;
      if (inpatientVisitId) {
        sessionStorage.setItem('highlightInpatientVisitId', inpatientVisitId.toString());
        const path = clinicId 
          ? `/medical/clinic/${clinicId}/appointment-management/inpatient`
          : '/medical/clinic/appointment-management/inpatient';
        navigate(path);
        if (onClose) onClose();
      } else if (actionUrl) {
        if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
          window.location.href = actionUrl;
        } else {
          navigate(actionUrl);
        }
        if (onClose) onClose();
      }
    } else if (actionUrl) {
      if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
        window.location.href = actionUrl;
      } else {
        navigate(actionUrl);
      }
      if (onClose) onClose();
    }
  }
}

export default VisitHandler;

